import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'next-themes';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toZonedTime, format } from 'date-fns-tz';

export function useThemeSchedule() {
  const { user } = useAuth();
  const { setTheme } = useTheme();

  const { data: preferences } = useQuery({
    queryKey: ['theme-preferences', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('theme_mode, timezone, dark_mode_start, dark_mode_end')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (!preferences) return;

    const { theme_mode, timezone, dark_mode_start, dark_mode_end } = preferences;

    // Handle non-scheduled modes immediately
    if (theme_mode !== 'scheduled') {
      setTheme(theme_mode);
      return;
    }

    // Handle scheduled mode
    const checkAndApplyScheduledTheme = () => {
      const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const now = new Date();
      const zonedNow = toZonedTime(now, userTimezone);
      const currentTime = format(zonedNow, 'HH:mm:ss', { timeZone: userTimezone });

      const isInDarkPeriod = (current: string, start: string, end: string) => {
        if (start < end) {
          // Same day (e.g., 09:00 to 17:00)
          return current >= start && current < end;
        } else {
          // Overnight (e.g., 20:00 to 07:00)
          return current >= start || current < end;
        }
      };

      const shouldBeDark = isInDarkPeriod(currentTime, dark_mode_start, dark_mode_end);
      setTheme(shouldBeDark ? 'dark' : 'light');
    };

    // Apply immediately
    checkAndApplyScheduledTheme();

    // Check every minute
    const interval = setInterval(checkAndApplyScheduledTheme, 60000);

    return () => clearInterval(interval);
  }, [preferences, setTheme]);

  return { preferences };
}
